import { forwardRef, useEffect, useImperativeHandle, useRef } from 'react';

// Cloudflare Turnstile (SC-170, replaces Google reCAPTCHA v2). Explicit render
// so we control mount/reset; the script is injected once and shared across
// instances. Managed widget → invisible/non-interactive for most visitors,
// challenge only for high-risk sessions.
const SCRIPT_ID = 'cf-turnstile-script';
const SCRIPT_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';

interface TurnstileRenderOptions {
  sitekey: string;
  callback?: (token: string) => void;
  'expired-callback'?: () => void;
  'error-callback'?: () => void;
  'timeout-callback'?: () => void;
  'unsupported-callback'?: () => void;
  'before-interactive-callback'?: () => void;
  'after-interactive-callback'?: () => void;
}

interface TurnstileApi {
  render: (el: HTMLElement, opts: TurnstileRenderOptions) => string;
  reset: (widgetId?: string) => void;
  remove: (widgetId?: string) => void;
}

declare global {
  interface Window {
    turnstile?: TurnstileApi;
  }
}

export interface TurnstileWidgetHandle {
  reset: () => void;
}

// DF-0419 — the three ways a widget leaves the visitor without a token AND
// without an `error`: the interactive check was shown but not solved in time,
// the browser is not supported, or nothing answered at all (script never loaded
// nor failed, challenge hanging). Each one used to be silent.
export type TurnstileStuckReason = 'timeout' | 'unsupported' | 'stalled';

// A managed widget resolves in one or two seconds; past this, the visitor is
// waiting on something that will not come by itself.
const STALL_MS = 12_000;

interface TurnstileWidgetProps {
  siteKey: string;
  onSuccess: (token: string) => void;
  onExpire?: () => void;
  onError?: () => void;
  onStuck?: (reason: TurnstileStuckReason) => void;
  /** true while Turnstile shows its « Vérifiez que vous êtes humain » box. */
  onInteractive?: (interactive: boolean) => void;
}

let scriptPromise: Promise<void> | null = null;

function loadTurnstileScript(): Promise<void> {
  if (window.turnstile) return Promise.resolve();
  if (scriptPromise) return scriptPromise;

  scriptPromise = new Promise<void>((resolve, reject) => {
    const existing = document.getElementById(SCRIPT_ID) as HTMLScriptElement | null;
    if (existing) {
      existing.addEventListener('load', () => resolve());
      existing.addEventListener('error', () => reject(new Error('turnstile script failed')));
      return;
    }
    const script = document.createElement('script');
    script.id = SCRIPT_ID;
    script.src = SCRIPT_SRC;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('turnstile script failed'));
    document.head.appendChild(script);
  }).catch((err) => {
    // SC-1270 — a failed load must not be cached: the rejected promise and the
    // dead <script> element would answer every later attempt (« Relancer la
    // vérification ») with the same failure, without a single new request.
    scriptPromise = null;
    document.getElementById(SCRIPT_ID)?.remove();
    throw err;
  });
  return scriptPromise;
}

const TurnstileWidget = forwardRef<TurnstileWidgetHandle, TurnstileWidgetProps>(
  function TurnstileWidget({ siteKey, onSuccess, onExpire, onError, onStuck, onInteractive }, ref) {
    const containerRef = useRef<HTMLDivElement>(null);
    const widgetIdRef = useRef<string | null>(null);
    const stallTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    // Latest-callback refs so the render effect depends only on siteKey and does
    // not re-mount the widget when the parent passes fresh inline handlers.
    const onSuccessRef = useRef(onSuccess);
    const onExpireRef = useRef(onExpire);
    const onErrorRef = useRef(onError);
    const onStuckRef = useRef(onStuck);
    const onInteractiveRef = useRef(onInteractive);
    onSuccessRef.current = onSuccess;
    onExpireRef.current = onExpire;
    onErrorRef.current = onError;
    onStuckRef.current = onStuck;
    onInteractiveRef.current = onInteractive;

    const clearStall = () => {
      if (stallTimerRef.current) clearTimeout(stallTimerRef.current);
      stallTimerRef.current = null;
    };
    // Armed whenever a token is being waited for (mount, reset, expiry). Any
    // answer from Turnstile disarms it; so does the interactive box, which is
    // waiting for the visitor, not stalled — Turnstile's own `timeout-callback`
    // covers that case.
    const armStall = () => {
      clearStall();
      stallTimerRef.current = setTimeout(() => {
        stallTimerRef.current = null;
        onStuckRef.current?.('stalled');
      }, STALL_MS);
    };

    useImperativeHandle(ref, () => ({
      reset: () => {
        if (widgetIdRef.current && window.turnstile) {
          window.turnstile.reset(widgetIdRef.current);
          armStall();
        } else {
          // SC-1270 — nothing was rendered (the script failed or never came):
          // a reset has no widget to act on, so load and render again.
          mount();
        }
      },
    }));

    const cancelledRef = useRef(false);
    const mount = () => {
      armStall();
      loadTurnstileScript()
        .then(() => {
          if (cancelledRef.current || widgetIdRef.current || !containerRef.current || !window.turnstile) return;
          widgetIdRef.current = window.turnstile.render(containerRef.current, {
            sitekey: siteKey,
            callback: (token: string) => {
              clearStall();
              onSuccessRef.current(token);
            },
            'expired-callback': () => {
              armStall();
              onExpireRef.current?.();
            },
            'error-callback': () => {
              clearStall();
              onErrorRef.current?.();
            },
            'timeout-callback': () => {
              clearStall();
              onStuckRef.current?.('timeout');
            },
            'unsupported-callback': () => {
              clearStall();
              onStuckRef.current?.('unsupported');
            },
            'before-interactive-callback': () => {
              clearStall();
              onInteractiveRef.current?.(true);
            },
            'after-interactive-callback': () => onInteractiveRef.current?.(false),
          });
        })
        .catch(() => {
          if (cancelledRef.current) return;
          clearStall();
          onErrorRef.current?.();
        });
    };

    useEffect(() => {
      cancelledRef.current = false;
      mount();

      return () => {
        cancelledRef.current = true;
        clearStall();
        if (widgetIdRef.current && window.turnstile) {
          window.turnstile.remove(widgetIdRef.current);
          widgetIdRef.current = null;
        }
      };
      // mount only touches refs: re-mounting the widget on its identity
      // would reset the challenge at every render.
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [siteKey]);

    return <div ref={containerRef} />;
  },
);

export default TurnstileWidget;
